import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react'
import { css } from '@emotion/core'
import { useLazyQuery, useQuery } from '@apollo/client'
import {
  BillboardText,
  colors,
  Divider,
  Expander,
  ExpanderHeader,
  ExpanderContent,
  H3,
  List,
  ListItem,
  Paragraph,
  Row,
  Separator,
  Skeleton,
} from '@fs/zion-ui'
import { setPreference, usePreference, preCachePreferences } from '@fs/zion-preferences'
import { DocumentBasic } from '@fs/zion-icon'
import { baseUrl } from '@fs/zion-config'
import { useGroupData } from '../../hooks'
import queries from '../coverages/queries'
import wikiQueries from '../researchWiki/queries'
import transl from './References.transl'
import CollectionsIcon from './CollectionsIcon'

const SIDESHEET_WIDTH = 327
const customStyle = css`
  & [class^='rowCss'] * {
    color: ${colors.blue60};
  }
  & [class^='rowCss'] *:hover {
    text-decoration: underline;
  }

  & [class^='secondaryText'] * {
    color: ${colors.transparent.gray40};
  }

  & [class^='listItemCss'] {
    padding-bottom: 16px;
  }
`
const badgeStyle = css`
  font-size: 0.8em;
  color: ${colors.transparent.gray80};
  padding-inline-start: 5px;
`

const REFERENCE_SECTION_STATES = {
  LOADING: 'LOADING',
  EMPTY: 'EMPTY',
  OK: 'OK',
}

function determineReferenceSectionDisplayState(loading, links, width) {
  if (loading) return REFERENCE_SECTION_STATES.LOADING
  if (Array.isArray(links) && links.length > 0 && width) return REFERENCE_SECTION_STATES.OK
  return REFERENCE_SECTION_STATES.EMPTY
}

const INITIALLY_OPEN_PREFERENCE_KEYS = {
  collections: 'lightyear.references.collections.initially-closed',
  wiki: 'lightyear.references.wiki.initially-closed',
}

Object.values(INITIALLY_OPEN_PREFERENCE_KEYS).forEach((preferenceKey) => {
  preCachePreferences(preferenceKey)
})

function ReferenceSection({ loading, links, listRef, width, isWiki, onWikiOpen }) {
  const preferenceKey = isWiki ? INITIALLY_OPEN_PREFERENCE_KEYS.wiki : INITIALLY_OPEN_PREFERENCE_KEYS.collections
  const title = isWiki ? transl.researchWiki() : transl.collections()
  const subtitle = isWiki ? transl.researchArticles() : transl.partOfCollections()
  const icon = isWiki ? DocumentBasic : CollectionsIcon
  const initiallyClosedPreference = usePreference(preferenceKey)
  const [userHasToggledOpenState, setUserHasToggledOpenState] = useState(false)
  const displayState = determineReferenceSectionDisplayState(loading, links, width)
  const [userSetOpen, setUserSetOpen] = useState(!isWiki)

  useEffect(() => {
    if (userSetOpen && isWiki) {
      onWikiOpen?.()
    }
  }, [userSetOpen, isWiki, onWikiOpen])

  let open = userSetOpen
  // if there are no links and the user hasn't opened the section
  if (!(displayState === REFERENCE_SECTION_STATES.OK || userHasToggledOpenState)) open = false
  // if the user preference is closed and the user hasn't opened the section
  if (!userHasToggledOpenState && initiallyClosedPreference) open = false

  const setOpen = () => {
    setPreference(preferenceKey, open)
    setUserSetOpen(!open)
    setUserHasToggledOpenState(true)
  }

  const getDisplayValue = () => {
    if (displayState === REFERENCE_SECTION_STATES.LOADING) {
      return null
    }

    if (!userSetOpen && isWiki) {
      return ''
    }

    return `(${links?.length ?? 0})`
  }

  return displayState === REFERENCE_SECTION_STATES.LOADING ? (
    <Skeleton.ListItem />
  ) : (
    <>
      <Expander open={open} setOpen={setOpen}>
        <ExpanderHeader data-testid={title} data-open={open}>
          {title}
          <span css={badgeStyle}> {getDisplayValue()}</span>
        </ExpanderHeader>
        <ExpanderContent>
          {displayState === REFERENCE_SECTION_STATES.OK && (
            <>
              <Paragraph size="xs">{subtitle}</Paragraph>
              <Separator size="nano" />
              <div css={customStyle}>
                <List ref={listRef}>
                  {links.filter(Boolean).map((link) => (
                    <ListItem
                      key={`${link.title}-${link.type}-${link.url}`}
                      dense
                      overline={isWiki ? transl.wikiPageSubTitle() : link.type}
                      primaryText={link.title}
                      secondaryText={link.secondaryText}
                      startElement={<ListItem.Icon color={colors.blue60} Icon={icon} />}
                      to={link.url}
                      target="_blank"
                      linkName={isWiki ? 'wiki-article' : 'record-collection'}
                    />
                  ))}
                </List>
              </div>
            </>
          )}
          {displayState === REFERENCE_SECTION_STATES.LOADING && (
            <Skeleton.ListItem hasIcon hasSecondLine maxWidth="100%" />
          )}
          {displayState === REFERENCE_SECTION_STATES.EMPTY && (
            <Row alignX="center">
              <BillboardText primaryColor={colors.transparent.gray40}>
                <Paragraph size="sm">{isWiki ? transl.noWikiPages() : transl.noCollections()}</Paragraph>
              </BillboardText>
            </Row>
          )}
        </ExpanderContent>
      </Expander>
      <Divider />
    </>
  )
}

export default function References({ isBottomSheet }) {
  const groupData = useGroupData()
  const group = useMemo(() => groupData?.group, [groupData])
  const listRef = useRef()
  const width = isBottomSheet ? listRef.current?.offsetWidth : SIDESHEET_WIDTH
  const [loadWikiLinks, setLoadWikiLinks] = useState(false)

  const [fetchGroupParents] = useLazyQuery(queries.getGroupParents())
  const [fetchCollection] = useLazyQuery(queries.getCollection())
  const placeIds = group?.coverages?.map((coverage) => coverage?.place?.id).filter(Boolean)
  const { data: wikiLinksData, loading: wikiLoading } = useQuery(wikiQueries.getWikiLinks(), {
    variables: { placeIds },
    skip: !placeIds?.length || !loadWikiLinks,
  })
  const wikiLinks = wikiLinksData?.wikiLinks || []

  const [collectionsLoading, setCollectionsLoading] = useState(false)
  const [collectionLinks, setCollectionLinks] = useState([])

  const imageGroupNumber = group?.imageGroupNumber || group?.metadata?.imageGroupNumber
  const groupId = group?.id

  const fetchCollections = useCallback(async () => {
    if (!(groupId && imageGroupNumber)) {
      setCollectionLinks([])
      return
    }

    setCollectionsLoading(true)
    const collectionsToDisplay = []

    try {
      const collectionsResponse = await fetchGroupParents({
        variables: { rmsId: groupId, parentGroupType: 'COLLECTION' },
      })

      const collections = collectionsResponse?.data?.groupParents
      const groupName = collections?.[0]?.groupName
      const catalogUrl = `${baseUrl}/search/catalog/results?count=20&query=%2Bfilm_number%3A${imageGroupNumber}`
      const collectionUrl = `${baseUrl}/search/collection/${groupName}`

      if (collections?.[0]?.title) {
        collectionsToDisplay.push({
          type: transl.catalogCollection(),
          title: collections[0].title,
          url: catalogUrl,
        })
      }

      if (groupName) {
        try {
          const collectionData = await fetchCollection({ variables: { collectionId: groupName } })
          const collectionTitle = collectionData?.data?.collection?.title
          if (collectionTitle) {
            collectionsToDisplay.push({
              type: transl.historicalRecordCollection(),
              title: collectionTitle,
              url: collectionUrl,
            })
          }
        } catch (error) {
          if (!error.graphQLErrors?.[0]?.message.includes('410')) {
            console.error(error)
          } else if (collectionsToDisplay.length > 0) {
            collectionsToDisplay.pop()
          }
        }
      }

      setCollectionLinks(collectionsToDisplay)
    } catch (e) {
      console.error(e)
      setCollectionLinks([])
    } finally {
      setCollectionsLoading(false)
    }
  }, [groupId, imageGroupNumber, fetchGroupParents, fetchCollection])

  useEffect(() => {
    fetchCollections()
    return () => {
      setCollectionsLoading(false)
      setCollectionLinks([])
    }
  }, [fetchCollections])
  const handleWikiOpen = useCallback(() => {
    setLoadWikiLinks(true)
  }, [])
  return (
    <div id="shared-image-viewer-references-section">
      <Separator />
      <H3 size="H6">{transl.references()}</H3>
      <ReferenceSection {...{ loading: collectionsLoading, links: collectionLinks, listRef, width }} />
      <ReferenceSection
        {...{ loading: wikiLoading, links: wikiLinks, listRef, width, isWiki: true, onWikiOpen: handleWikiOpen }}
      />
      <Separator />
    </div>
  )
}
